import React, { useMemo } from 'react';
import { Divider, Flex, Tooltip } from 'antd';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { keyBy } from 'lodash-es';

import { BenchmarkClient, StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExText } from 'jf/components/DevExText';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { BenchmarkPercentile } from '../benchmark/BenchmarkSelect';
import { ScoreBlock } from '../ScoreBlock';
import { useStudyScores } from '../useStudyScores';

import { BenchmarkCell } from './BenchmarkCell';
import { TrendCell } from './TrendCell';
import { useStudyDevExIndex } from './useStudyDevExIndex';

type OverallStudyScoreCardProps = {
  studyRef: string;
  prevStudyRef?: string;
  teamRef?: string;
  benchmarkPercentile?: BenchmarkPercentile;
};

export const OverallStudyScoreCard: React.FC<OverallStudyScoreCardProps> = ({
  studyRef,
  teamRef,
  benchmarkPercentile,
}) => {
  const theme = useDevExTheme();

  const { data: studyScoresByTopic } = useStudyScores({
    studyRef: studyRef,
    teamRef: teamRef,
    type: 'TOPIC',
  });
  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef });

  const { devexIndex } = useStudyDevExIndex({
    studyRef,
    teamRef,
  });
  const { devexIndex: nextDevexIndex } = useStudyDevExIndex({
    studyRef: study?.lastStudyRef,
    teamRef,
  });

  const showBenchmarks = !!window.dx.user?.company.aggregatable && !!benchmarkPercentile;

  const { data: cohorts } = useClientQuery(BenchmarkClient.getBenchmarkCohorts, null, {
    enabled: showBenchmarks,
  });
  const cohort = cohorts?.at(-1); // grab the most recent cohort
  const { data: cohortScores } = useClientQuery(
    BenchmarkClient.getBenchmarkCohortScores,
    { slug: cohort?.slug, type: 'TOPIC' },
    { enabled: showBenchmarks }
  );

  const benchmarkAvg: number | undefined = useMemo(() => {
    let benchmarkTotal;
    let nobenchmark = 0;
    const benchmarkBySlug = keyBy(cohortScores, 'slug');

    if (studyScoresByTopic && benchmarkPercentile) {
      benchmarkTotal = studyScoresByTopic.reduce((total, topic) => {
        if (benchmarkBySlug[topic.slug]) {
          return (total += benchmarkBySlug[topic.slug][benchmarkPercentile]);
        }
        nobenchmark++;
        return total;
      }, 0);
      if (nobenchmark === studyScoresByTopic.length) {
        // no benchmarks
        return undefined;
      }
      return Math.round(benchmarkTotal / (studyScoresByTopic.length - nobenchmark));
    }
    return undefined;
    // benchmark percentile causes the data to change
    // this gets triggered by that chain, not benchmarkPercentile
  }, [studyScoresByTopic, cohortScores]);

  const trendval = useMemo(() => {
    if (typeof devexIndex === 'number' && typeof nextDevexIndex === 'number') {
      return devexIndex - nextDevexIndex;
    }
    return undefined;
  }, [devexIndex, nextDevexIndex]);
  return (
    <DevExCard style={{ width: 360 }}>
      <Flex align="center" gap={theme.variable.spacing.md}>
        <ScoreBlock score={devexIndex ?? undefined} size="lg" />
        <Flex vertical gap={theme.variable.spacing.sm}>
          <Flex gap={theme.variable.spacing.sm} align="center">
            <DevExText strong fontSize="lg">
              DevEx Index
            </DevExText>

            <Tooltip
              overlayStyle={{ width: 400 }}
              title={
                <>
                  The <strong>DevEx Index</strong> is a single metric summarizing your overall
                  developer experience, calculated as an average of topic scores from your survey.
                </>
              }
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Tooltip>
          </Flex>
          <Flex gap={theme.variable.spacing.sm} align="center">
            <DevExText type="secondary">Trend</DevExText>
            <TrendCell trend={trendval} />

            {benchmarkPercentile && (
              <>
                <Divider type="vertical" />
                <DevExText type="secondary">Benchmark</DevExText>
                <BenchmarkCell
                  benchmark={!!benchmarkAvg && !!devexIndex ? devexIndex - benchmarkAvg : undefined}
                />
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </DevExCard>
  );
};
